import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { logAirportFilter } from '../../../../../common/log-ga-event'
import { updateAirportsFiltersReq } from '../../../../reducers/filters/filterInfo/filterInfo'
import { updateAirportsFiltersUI } from '../../../../reducers/filters/airportsFilter/airportsFilter'

export const useAirportsFilters = ({ changeHandler, isDynamic = false, isRecommended = false }) => {
  const dispatch = useDispatch()

  const { airports, activeTabId } = useSelector((state) => {
    return {
      airports: state?.airportsFilter || [],
      activeTabId: state?.activeTabId,
    }
  })

  const cheapestPriceObj = useSelector((state) => state?.cheapestPrice || [])
  const cheapestPriceBySliceId = cheapestPriceObj.find((i) => i.sliceId === activeTabId - 1)

  const delayedStateUpdate = useCallback(
    (shouldFireChange = true) => {
      return (...args) => {
        const [payload, entity, meta] = args
        dispatch(
          updateAirportsFiltersReq({
            payload,
            meta,
            entity,
            sbsParams: {
              activeTabId,
            },
          })
        )

        // should not fire the listings call when the user selected none
        if (shouldFireChange && changeHandler) {
          changeHandler()
        }
      }
    },
    [dispatch, activeTabId, changeHandler]
  )

  const baseAirportHandler = useCallback(
    (entity) => {
      return (changed) => {
        const key = Object.keys(changed)[0]
        const payload = [key, changed[key]]
        const enabledFlag = changed[key]
        dispatch(updateAirportsFiltersUI({ payload, meta: {}, entity }))
        if (!isRecommended) {
          logAirportFilter({ airport: key, isDynamic, enabled: enabledFlag })
        }
        delayedStateUpdate(true)(payload, entity, {})
      }
    },
    [dispatch, isRecommended, delayedStateUpdate, isDynamic]
  )

  const onSelectAllClick = useCallback(() => {
    dispatch(updateAirportsFiltersUI({ payload: [], meta: { isAllClick: true }, entity: 'dest' }))
    dispatch(
      updateAirportsFiltersUI({
        payload: [],
        meta: {
          isAllClick: true,
        },
        entity: 'origin',
      })
    )
    delayedStateUpdate(true)([], null, { isAllClick: true })
  }, [dispatch, delayedStateUpdate])

  const onSelectNoneClick = useCallback(
    ({ shouldFireChange = true } = {}) => {
      dispatch(updateAirportsFiltersUI({ payload: [], meta: { isNoneClick: true }, entity: 'dest' }))
      dispatch(updateAirportsFiltersUI({ payload: [], meta: { isNoneClick: true }, entity: 'origin' }))
      delayedStateUpdate(shouldFireChange)([], null, { isNoneClick: true })
    },
    [dispatch, delayedStateUpdate]
  )

  const handleOnlyClick = (entity) => {
    return ({ value }) => {
      const payload = [value, true]

      if (!isRecommended) logAirportFilter({ airport: value, isOnly: true })
      dispatch(updateAirportsFiltersUI({ payload, meta: { isOnlyClick: true }, entity }))
      delayedStateUpdate(true)(payload, entity, { isOnlyClick: true })
    }
  }

  const originFilter = {
    handleChange: baseAirportHandler('origin'),
    filters: [],
  }

  const destinationFilter = {
    handleChange: baseAirportHandler('dest'),
    filters: [],
  }

  if (Array.isArray(airports)) {
    airports.forEach((slice, index, list) => {
      const notEndofMultiSlice = list.length === 1 || index < list.length - 1

      if (notEndofMultiSlice) {
        const origAirports = slice.originAirports

        const destAirports = slice.destinationAirports

        originFilter.filters = originFilter.filters.concat(origAirports)
        destinationFilter.filters = destinationFilter.filters.concat(destAirports)
      }
    })
  }

  const isSelectAllDisabled =
    originFilter?.filters?.filter((i) => i.checked).length === originFilter?.filters?.length &&
    destinationFilter?.filters?.filter((i) => i.checked).length === destinationFilter?.filters?.length
  const isSelectNoneDisabled =
    originFilter?.filters?.filter((i) => i.checked).length === 0 &&
    destinationFilter?.filters?.filter((i) => i.checked).length === 0

  const applyIsCheapest = (filters) =>
    filters.map((i) => ({
      ...i,
      isCheapest:
        Number.parseInt(i.secondaryLabel?.replaceAll(/\D/g, ''), 10) ===
        Math.ceil(cheapestPriceBySliceId?.cheapestPrice),
    }))

  if (originFilter?.filters) {
    originFilter.filters = applyIsCheapest(originFilter.filters)
  }
  if (destinationFilter?.filters) {
    destinationFilter.filters = applyIsCheapest(destinationFilter.filters)
  }

  return {
    originFilter,
    destinationFilter,
    handleOnlyClick,
    onSelectAllClick,
    onSelectNoneClick,
    isSelectAllDisabled,
    isSelectNoneDisabled,
  }
}
